import { createRequest, type RequestPlugin } from '@direct-frontend/modules-request';
import { getRequestContext, setRequestContext } from '../../../../lib/context';
import { CSRF_HEADER_NAME, MAX_TRIES_COUNT } from './constants';
import { getCSRFTokenRequest } from './getCSRFTokenRequest';

type CSRFClientPluginOptions = {
    try: number;
};

export type CSRFClientPlugin = () => Required<
    Pick<RequestPlugin<CSRFClientPluginOptions>, 'transformOptions' | 'transformResponse'>
>;

export const getCSRFClientPlugin: CSRFClientPlugin = () => {
    return {
        transformOptions(options) {
            const { csrfToken } = getRequestContext();

            if (!csrfToken) {
                return options;
            }

            return {
                ...options,
                headers: {
                    ...options.headers,
                    [CSRF_HEADER_NAME]: csrfToken,
                },
                pluginOptions: {
                    ...options.pluginOptions,
                    try: 1,
                },
            };
        },
        async transformResponse(response, meta) {
            const { data, status } = response;
            const { rawOptions, initOptions } = meta;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic response
            const isInvalidCSRFToken = status === 403 && (data as any)?.code === 'invalid_csrf';

            if (!isInvalidCSRFToken || (rawOptions.pluginOptions?.try ?? 0) >= MAX_TRIES_COUNT) {
                return response;
            }

            let csrfToken;

            try {
                csrfToken = (await getCSRFTokenRequest()).data?.token;

                if (!csrfToken) {
                    return response;
                }

                setRequestContext({ ...getRequestContext(), csrfToken });
            } catch {
                return response;
            }

            const newRawOptions = {
                ...rawOptions,
                headers: {
                    ...rawOptions.headers,
                    [CSRF_HEADER_NAME]: csrfToken,
                },
                pluginOptions: {
                    ...rawOptions.pluginOptions,
                    try: (rawOptions.pluginOptions?.try ?? 1) + 1,
                },
            };

            // Создаем новый запрос с теми же настройками и плагинами
            const clientRequest = createRequest(initOptions);

            // Выполняем перезапрос и возвращаем его результат
            // transformResponse вызывается ДО обработки ответа RTK Query,
            // поэтому результат перезапроса должен попасть в кэш
            const retryResponse = await clientRequest(newRawOptions);

            return retryResponse;
        },
    };
};
